"use client";

import { memo, useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight, Trash2, X } from "lucide-react";
import { useTranslation } from "@/lib/i18n";
import { useTheme } from "@/lib/theme";
import { useConversation, type Appearance } from "@/lib/chat/conversation";
import Composer from "./Composer";
import MessageList from "./MessageList";
import Suggestions from "./Suggestions";
import { useMeasureNotice } from "./restingHeight";

/**
 * The assistant, in whichever appearance the provider currently reports.
 *
 * The anatomy never changes: header · conversation · composer · CTA. Appearance
 * A is simply this panel with no conversation and no CTA; C is the same panel
 * narrower. See specs/ai-assistant — "The assistant is one conversation in four
 * appearances".
 */
function AssistantPanel({
  appearance,
  narrow,
  recordRetentionDays,
}: {
  appearance: Appearance;
  /** The viewport is below AssistantHost's `isNarrow` breakpoint. */
  narrow: boolean;
  /** Days a recorded conversation is kept, as the sweep enforces it. */
  recordRetentionDays: number;
}) {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { messages, close, clear } = useConversation();
  const inputRef = useRef<HTMLInputElement>(null);

  const isOpen = appearance === "inline-open" || appearance === "docked";
  const compact = appearance === "docked";
  const hasConversation = messages.length > 0;
  // Inline, the panel is the slot's width, so the notice's height here is the
  // one the resting Ask-Bar has to make room for.
  const noticeRef = useMeasureNotice(
    appearance === "inline" || appearance === "inline-open",
  );

  // Opening moves focus to the composer.
  useEffect(() => {
    if (isOpen) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 180);
      return () => window.clearTimeout(id);
    }
  }, [isOpen]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-assistant-divider bg-assistant-header px-4 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <Image
            src={
              theme === "dark"
                ? "/haintz-it-solutions/logo-white.svg"
                : "/haintz-it-solutions/logo.svg"
            }
            alt=""
            aria-hidden="true"
            width={24}
            height={24}
            className="h-4 w-6 shrink-0 object-contain"
          />
          <span className="truncate font-mono text-xs font-bold tracking-widest uppercase text-text-primary">
            {t("chat.panel.title")}
          </span>
        </div>

        {/*
          Resting with a retained conversation still shows the clear control —
          it is the only way to discard it — but never a close control, because
          there is nothing open to close. The status line appears only on a
          genuinely fresh Ask-Bar.
        */}
        {isOpen || hasConversation ? (
          <div className="flex shrink-0 items-center gap-3">
            {hasConversation && (
              <button
                type="button"
                onClick={clear}
                aria-label={t("chat.clear")}
                title={t("chat.clear")}
                className="text-text-faint transition-colors hover:text-text-primary"
              >
                <Trash2 size={14} aria-hidden="true" />
              </button>
            )}
            {isOpen && (
              <button
                type="button"
                onClick={close}
                aria-label={t("chat.close")}
                className="text-text-faint transition-colors hover:text-text-primary"
              >
                <X size={16} aria-hidden="true" />
              </button>
            )}
          </div>
        ) : (
          <span className="shrink-0 font-mono text-[10px] tracking-[0.16em] uppercase text-text-muted">
            {t("chat.askbar.status")}
          </span>
        )}
      </div>

      {/* Conversation area — absent entirely in the resting Ask-Bar */}
      {isOpen && (
        <>
          {hasConversation && (
            <MessageList compact={compact} narrow={narrow} />
          )}
          <Suggestions compact={compact} />
          <div className="shrink-0 border-t border-assistant-divider" />
        </>
      )}

      {/* Composer — always the bottom row */}
      <Composer
        ref={inputRef}
        compact={compact}
        /*
          Keyed off the appearance, not off whether a conversation exists. While
          it is resting it IS the Ask-Bar, so it keeps the inviting placeholder
          even with a conversation behind it — "Nachricht eingeben..." made a
          returning visitor's Ask-Bar read like a spent chat box. The terser
          placeholder belongs to the opened panel, where the conversation is
          already on screen and the field is plainly a reply box.
        */
        placeholderKey={
          isOpen && hasConversation
            ? "chat.input.placeholder"
            : "chat.askbar.placeholder"
        }
        /*
          Only the untouched resting Ask-Bar types its own example questions.
          Once the panel is open the conversation is the invitation, and once
          there is a conversation behind the bar the visitor has already been
          invited — writing questions into the field at that point would read
          as the assistant talking over them.
        */
        invite={!isOpen && !hasConversation}
      />

      {/*
        What happens to what the visitor types, where they type it.

        This is the Art. 13 DSGVO information at the moment of collection: it
        sits under the composer in every appearance that has one, so it is read
        before the first message rather than found afterwards in the privacy
        notice. The period comes from the one constant the retention sweep also
        reads, so the sentence cannot drift from what is enforced, and the
        line is unconditional because the recording is: the server refuses to
        start without storage for it, so there is no deployment this sentence
        could be wrong about. See specs/ai-transparency.
      */}
      <p
        ref={noticeRef}
        className="shrink-0 border-t border-assistant-divider bg-assistant-header px-4 py-2 text-[10px] leading-snug text-text-faint"
      >
        {t("chat.recording.notice").replace(
          "{days}",
          String(recordRetentionDays),
        )}{" "}
        <Link
          href="/nutzungsbedingungen"
          className="underline hover:text-mint-text"
        >
          {t("chat.recording.rules")}
        </Link>
        {" · "}
        <Link href="/datenschutz" className="underline hover:text-mint-text">
          {t("chat.recording.privacy")}
        </Link>
      </p>

      {/* CTA — only once there is something to convert on */}
      {isOpen && hasConversation && (
        <Link
          href="/#contact"
          onClick={close}
          className="flex shrink-0 items-center justify-center gap-2 border-t border-assistant-divider bg-assistant-header px-4 py-2.5 font-mono text-[10px] font-bold tracking-widest uppercase text-mint-text transition-colors hover:bg-mint/10"
        >
          {t("chat.cta")}
          <ArrowRight size={10} aria-hidden="true" />
        </Link>
      )}
    </div>
  );
}

/*
 * Memoised because the host re-renders on every frame of a scroll — it is
 * following the slot — while nothing in here depends on where the panel is.
 * Without this, one scroll re-rendered the header, the composer, the notice and
 * their next/image and next/link children sixty times a second, which is what
 * made the bar feel heavy even after the position itself became cheap.
 */
export default memo(AssistantPanel);
